import { addDays, comparePlainDate, daysBetween, plainDateFromIso, plainDateToIso, } from '../custody/time-zone.js';
import { SCHOOL_HOLIDAY_PERIODS } from './school-holidays.data.js';
export class SchoolCalendarCoverageError extends Error {
    constructor(message) {
        super(message);
        this.name = 'SchoolCalendarCoverageError';
    }
}
const PERIODS = SCHOOL_HOLIDAY_PERIODS.map((period) => ({
    schoolYear: period.schoolYear,
    zone: period.zone,
    name: period.name,
    from: plainDateFromIso(period.from),
    to: plainDateFromIso(period.to),
    // Une période d'un seul jour intitulée « Début des … » n'est qu'un marqueur de départ.
    openEnded: period.from === period.to && period.name.startsWith('Début'),
}));
/** Bornes réellement couvertes par les données figées. */
export function schoolCalendarCoverage() {
    const sorted = [...PERIODS].sort((a, b) => comparePlainDate(a.from, b.from));
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    if (!first || !last)
        throw new SchoolCalendarCoverageError('Aucune donnée de calendrier scolaire.');
    return {
        from: first.from,
        to: last.to,
        schoolYears: [...new Set(PERIODS.map((p) => p.schoolYear))].sort(),
    };
}
export function schoolHolidaysForZone(zone) {
    return PERIODS.filter((period) => period.zone === zone).sort((a, b) => comparePlainDate(a.from, b.from));
}
/**
 * Période de vacances contenant une date, ou `null` si l'école a lieu ce jour-là.
 * Lève quand la date sort des années figées : mieux vaut une erreur visible qu'un
 * calendrier de garde silencieusement faux.
 */
export function schoolHolidayAt(zone, date) {
    const coverage = schoolCalendarCoverage();
    if (comparePlainDate(date, coverage.from) < 0 || comparePlainDate(date, coverage.to) > 0) {
        throw new SchoolCalendarCoverageError(`Le ${plainDateToIso(date)} sort du calendrier scolaire figé ` +
            `(${plainDateToIso(coverage.from)} → ${plainDateToIso(coverage.to)}). ` +
            'Relancer `node scripts/fetch-school-holidays.mjs` quand le ministère aura publié la suite.');
    }
    return (schoolHolidaysForZone(zone).find((period) => comparePlainDate(date, period.from) >= 0 &&
        (period.openEnded || comparePlainDate(date, period.to) < 0)) ?? null);
}
export function isSchoolHoliday(zone, date) {
    return schoolHolidayAt(zone, date) !== null;
}
/**
 * Les jours de vacances d'un intervalle, en clés ISO — **sans jamais lever**.
 *
 * `schoolHolidayAt` lève hors couverture, et c'est juste pour un calcul de garde : un
 * partage de vacances faux est pire qu'un refus. Mais un **affichage** n'a pas le même
 * contrat. Le calendrier laisse feuilleter les mois librement, et une exception à chaque
 * clic sur « mois suivant » remplacerait un calendrier utile par un écran d'erreur.
 *
 * Hors des années figées, la fonction rend donc un ensemble vide : l'écran ne montre rien
 * plutôt que d'inventer des vacances. C'est la même règle que partout ailleurs — le
 * produit se tait quand il ne sait pas.
 *
 * Les bornes sont incluses. Une période ouverte (`openEnded`, dont la rentrée suivante
 * n'est pas publiée) ne colore rien au-delà de son premier jour : sa fin est inconnue, et
 * la deviner peindrait un été entier au hasard.
 */
export function schoolHolidayDays(zone, from, to) {
    const jours = new Set();
    const periodes = schoolHolidaysForZone(zone).filter((periode) => !periode.openEnded);
    for (const periode of periodes) {
        // Rien à faire d'une période entièrement hors de la fenêtre demandée.
        if (comparePlainDate(periode.to, from) <= 0 || comparePlainDate(periode.from, to) > 0)
            continue;
        let jour = comparePlainDate(periode.from, from) >= 0 ? periode.from : from;
        // `to` de la période est le jour de **reprise** : il est exclu des vacances.
        while (comparePlainDate(jour, periode.to) < 0 && comparePlainDate(jour, to) <= 0) {
            jours.add(plainDateToIso(jour));
            jour = addDays(jour, 1);
        }
    }
    return jours;
}
/** Nombre de jours de vacances d'une période. Refuse une période ouverte. */
export function periodLengthInDays(period) {
    assertClosed(period);
    return daysBetween(period.from, period.to);
}
export function assertClosed(period) {
    if (period.openEnded) {
        throw new SchoolCalendarCoverageError(`La période « ${period.name} » (${period.schoolYear}, zone ${period.zone}) n'a pas de fin connue : ` +
            'la rentrée suivante n\'est pas encore publiée.');
    }
}
/** Dernier jour de vacances (la veille de la reprise). */
export function lastHolidayDay(period) {
    assertClosed(period);
    return addDays(period.to, -1);
}
//# sourceMappingURL=school-holidays.js.map